import { CheckCircle2, ExternalLink, Image as ImageIcon, Loader2, Megaphone, Pause, Play, Sparkles, XCircle } from 'lucide-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { createBanner, getMyAdCredits, listMyBanners, pauseBanner, resumeBanner } from '@/api/member';

const BANNER_W = 468;
const BANNER_H = 60;

const STATUS_STYLES = {
  pending_approval: { label: 'Pending Approval', color: '#FFC20E' },
  active: { label: 'Active', color: '#10B981' },
  paused: { label: 'Paused', color: '#9CA3AF' },
  exhausted: { label: 'Exhausted', color: '#9CA3AF' },
  rejected: { label: 'Rejected (refunded)', color: '#EF4444' },
  disabled: { label: 'Disabled by Admin', color: '#EF4444' },
};

const isValidHttpsUrl = (s) => {
  if (!s || typeof s !== 'string') return false;
  try {
    const u = new URL(s.trim());
    return u.protocol === 'https:';
  } catch {
    return false;
  }
};

const BannerBuilder = ({ credits, onCreated }) => {
  const [imageUrl, setImageUrl] = useState('');
  const [destUrl, setDestUrl] = useState('');
  const [impressions, setImpressions] = useState(100);
  const [previewState, setPreviewState] = useState('idle'); // idle | loading | valid | invalid | wrong-size
  const [previewDims, setPreviewDims] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const imgRef = useRef(null);
  // Re-mount the <img> on URL change by using key=url — forces fresh load.

  // When image url changes, transition to loading state.
  useEffect(() => {
    if (!isValidHttpsUrl(imageUrl)) {
      setPreviewState(imageUrl ? 'invalid' : 'idle');
      setPreviewDims(null);
      return;
    }
    setPreviewState('loading');
    setPreviewDims(null);
  }, [imageUrl]);

  const handleImgLoad = useCallback((e) => {
    const w = e.currentTarget.naturalWidth;
    const h = e.currentTarget.naturalHeight;
    setPreviewDims({ w, h });
    if (w === BANNER_W && h === BANNER_H) {
      setPreviewState('valid');
    } else {
      setPreviewState('wrong-size');
    }
  }, []);

  const handleImgError = useCallback(() => {
    setPreviewState('invalid');
    setPreviewDims(null);
  }, []);

  const destValid = isValidHttpsUrl(destUrl);
  const impInt = Math.max(0, Math.floor(Number(impressions) || 0));
  const impressionsValid = impInt > 0 && impInt <= credits;
  const canSubmit = previewState === 'valid' && destValid && impressionsValid && !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      await createBanner({
        image_url: imageUrl.trim(),
        destination_url: destUrl.trim(),
        impressions: impInt,
        width: BANNER_W,
        height: BANNER_H,
      });
      toast.success(`Banner submitted — ${impInt.toLocaleString()} credits redeemed. Awaiting admin approval.`);
      setImageUrl('');
      setDestUrl('');
      setImpressions(100);
      setPreviewState('idle');
      setPreviewDims(null);
      onCreated?.();
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const code = typeof detail === 'object' ? detail?.code || detail?.message : detail || err.message;
      toast.error(`Submit failed: ${code}`);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="sc-glass-strong rounded-2xl p-5 md:p-7" data-testid="banner-builder">
      <div className="sc-pill">
        <span className="sc-dot" />
        Banner Builder
      </div>
      <h3 className="font-display font-bold text-xl md:text-2xl mt-3">
        Design Your <span className="sc-gold-text">468 × 60</span> Banner
      </h3>
      <p className="text-[13px] text-[var(--sc-text-dim)] mt-1 max-w-2xl">
        Paste a hosted image URL and a destination URL. The live preview renders below — the submit button unlocks only when the image loads successfully at
        exactly 468 × 60 px.
      </p>

      <div className="mt-5 grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-5 items-start">
        <div className="space-y-4">
          <div>
            <label htmlFor="banner-image-url" className="block text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
              Image URL (https · 468 × 60 px)
            </label>
            <input
              id="banner-image-url"
              type="text"
              value={imageUrl}
              onChange={(e) => setImageUrl(e.target.value)}
              placeholder="https://example.com/banner-468x60.png"
              className="sc-input w-full"
              data-testid="advertise-image-url-input"
              spellCheck={false}
            />
          </div>
          <div>
            <label htmlFor="banner-dest-url" className="block text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
              Destination URL (https — where clicks go)
            </label>
            <input
              id="banner-dest-url"
              type="text"
              value={destUrl}
              onChange={(e) => setDestUrl(e.target.value)}
              placeholder="https://your-site.com/landing"
              className="sc-input w-full"
              data-testid="advertise-dest-url-input"
              spellCheck={false}
            />
          </div>
          <div>
            <label htmlFor="banner-impressions" className="block text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)] mb-1">
              Impressions to redeem (1 credit = 1 impression)
            </label>
            <div className="flex items-center gap-2">
              <input
                id="banner-impressions"
                type="number"
                min={1}
                max={credits}
                step={100}
                value={impressions}
                onChange={(e) => setImpressions(e.target.value)}
                className="sc-input w-full sc-tabular"
                data-testid="advertise-impressions-input"
              />
              <button
                type="button"
                onClick={() => setImpressions(credits)}
                className="sc-btn-ghost shrink-0"
                style={{ padding: '10px 14px', fontSize: 11 }}
                data-testid="advertise-max-credits-btn"
              >
                Max ({credits.toLocaleString()})
              </button>
            </div>
            <div className="mt-2 text-[11px] text-[var(--sc-text-mute)] flex flex-wrap gap-3">
              <span>
                Balance: <span className="sc-tabular text-[#FFC20E] font-semibold">{credits.toLocaleString()}</span> credits
              </span>
              <span>•</span>
              <span>
                Cost: <span className="sc-tabular text-[#FFC20E] font-semibold">{impInt.toLocaleString()}</span> credits
              </span>
            </div>
          </div>
        </div>

        <div
          className="rounded-xl border p-3 flex flex-col items-center gap-2 self-start"
          style={{
            background: 'rgba(0,0,0,0.32)',
            borderColor:
              previewState === 'valid'
                ? 'rgba(16,185,129,0.45)'
                : previewState === 'invalid' || previewState === 'wrong-size'
                  ? 'rgba(239,68,68,0.45)'
                  : 'rgba(255,182,18,0.18)',
          }}
          data-testid="advertise-preview-container"
        >
          <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Live Preview — 468 × 60</div>
          <div
            className="rounded-md overflow-hidden bg-[rgba(0,0,0,0.6)] flex items-center justify-center"
            style={{ width: '100%', maxWidth: BANNER_W, aspectRatio: `${BANNER_W} / ${BANNER_H}` }}
          >
            {previewState === 'idle' && (
              <span className="text-[11px] text-[var(--sc-text-mute)] flex items-center gap-2">
                <ImageIcon className="w-4 h-4" /> Paste an image URL
              </span>
            )}
            {previewState === 'loading' && <Loader2 className="w-4 h-4 animate-spin text-[#FFC20E]" />}
            {(previewState === 'valid' || previewState === 'wrong-size') && (
              <img
                ref={imgRef}
                key={imageUrl /* force fresh load on url change */}
                src={imageUrl}
                alt="Banner preview"
                width={BANNER_W}
                height={BANNER_H}
                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                onLoad={handleImgLoad}
                onError={handleImgError}
                data-testid="advertise-preview-img"
                referrerPolicy="no-referrer"
              />
            )}
            {previewState === 'invalid' && (
              <span className="text-[11px] text-red-300 flex items-center gap-2 px-2 text-center">
                <XCircle className="w-4 h-4" /> Image failed to load
              </span>
            )}
            {/* Hidden loader image so onError still fires even when not displayed */}
            {previewState === 'loading' && (
              <img
                key={`probe-${imageUrl}`}
                src={imageUrl}
                alt=""
                style={{ display: 'none' }}
                onLoad={handleImgLoad}
                onError={handleImgError}
                referrerPolicy="no-referrer"
              />
            )}
          </div>
          {previewState === 'valid' && (
            <span className="text-[11px] text-emerald-300 flex items-center gap-1" data-testid="advertise-preview-valid">
              <CheckCircle2 className="w-3.5 h-3.5" /> Valid banner · ready to submit
            </span>
          )}
          {previewState === 'wrong-size' && previewDims && (
            <span className="text-[11px] text-red-300 flex items-center gap-1 text-center px-2" data-testid="advertise-preview-wrong-size">
              <XCircle className="w-3.5 h-3.5" /> Wrong size: {previewDims.w}×{previewDims.h} · Need 468×60
            </span>
          )}
          {previewState === 'invalid' && (
            <span className="text-[11px] text-red-300 flex items-center gap-1" data-testid="advertise-preview-invalid">
              <XCircle className="w-3.5 h-3.5" /> Submit blocked
            </span>
          )}
        </div>
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={!canSubmit}
          className="sc-btn-white"
          style={{ padding: '12px 18px', fontSize: 12 }}
          data-testid="advertise-submit-btn"
        >
          {submitting ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Megaphone className="w-3.5 h-3.5" />}
          Submit Banner ({impInt.toLocaleString()} credits)
        </button>
        {!destValid && destUrl && <span className="text-[11px] text-red-300">Destination URL must start with https://</span>}
        {!impressionsValid && credits > 0 && (
          <span className="text-[11px] text-red-300">Impressions must be between 1 and your balance ({credits.toLocaleString()})</span>
        )}
        {credits === 0 && (
          <span className="text-[11px] text-[var(--sc-text-mute)]">Earn credits by purchasing positions or sub packs on the Purchase / Deposit page.</span>
        )}
      </div>
    </section>
  );
};

const MyBannersList = ({ rows, onPause, onResume }) => {
  if (!rows || rows.length === 0) {
    return (
      <section className="sc-glass rounded-2xl p-6 text-center" data-testid="my-banners-empty">
        <div className="text-[12px] text-[var(--sc-text-mute)]">You haven't submitted any banners yet.</div>
      </section>
    );
  }

  return (
    <section className="sc-glass rounded-2xl p-5 md:p-6" data-testid="my-banners">
      <div className="flex items-center justify-between mb-4">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">My Banners</div>
      </div>
      <ul className="space-y-3">
        {rows.map((b) => {
          const style = STATUS_STYLES[b.status] || {
            label: b.status,
            color: '#9CA3AF',
          };
          return (
            <li
              key={b.id}
              className="rounded-xl border p-3 md:p-4 flex flex-col md:flex-row gap-4"
              style={{
                background: 'rgba(0,0,0,0.32)',
                borderColor: 'rgba(255, 182, 18, 0.18)',
              }}
              data-testid={`my-banner-${b.id}`}
            >
              <div
                className="shrink-0 rounded-md overflow-hidden bg-black"
                style={{ width: '100%', maxWidth: BANNER_W, aspectRatio: `${BANNER_W} / ${BANNER_H}` }}
              >
                <img
                  src={b.image_url}
                  alt=""
                  width={BANNER_W}
                  height={BANNER_H}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'contain',
                  }}
                  loading="lazy"
                  referrerPolicy="no-referrer"
                />
              </div>
              <div className="flex-1 min-w-0 grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Status</div>
                  <div className="text-[12px] font-semibold mt-1" style={{ color: style.color }}>
                    {style.label}
                  </div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Impressions Served</div>
                  <div className="text-[13px] sc-tabular font-semibold mt-1 sc-gold-text">{b.impressions_served.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Remaining</div>
                  <div className="text-[13px] sc-tabular font-semibold mt-1 sc-gold-text">{b.impressions_remaining.toLocaleString()}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Clicks · CTR</div>
                  <div className="text-[13px] sc-tabular font-semibold mt-1 sc-gold-text">
                    {b.clicks.toLocaleString()} · {b.ctr_pct.toFixed(2)}%
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {b.status === 'active' && (
                  <button
                    type="button"
                    onClick={() => onPause(b.id)}
                    className="sc-btn-ghost"
                    style={{ padding: '8px 12px', fontSize: 11 }}
                    data-testid={`pause-banner-${b.id}`}
                  >
                    <Pause className="w-3 h-3" />
                    Pause
                  </button>
                )}
                {b.status === 'paused' && (
                  <button
                    type="button"
                    onClick={() => onResume(b.id)}
                    className="sc-btn-ghost"
                    style={{ padding: '8px 12px', fontSize: 11 }}
                    data-testid={`resume-banner-${b.id}`}
                  >
                    <Play className="w-3 h-3" />
                    Resume
                  </button>
                )}
                <a
                  href={b.destination_url}
                  target="_blank"
                  rel="noopener noreferrer nofollow"
                  className="text-[10px] uppercase tracking-[0.18em] text-[var(--sc-text-mute)] hover:text-[#FFC20E] inline-flex items-center gap-1"
                >
                  <ExternalLink className="w-3 h-3" /> Link
                </a>
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
};

const AdvertiseSubpage = ({ me }) => {
  const [credits, setCredits] = useState(Number(me?.ad_credits_balance || 0));
  const [rows, setRows] = useState([]);
  const [, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const [c, list] = await Promise.all([getMyAdCredits(), listMyBanners(1)]);
      setCredits(Number(c?.ad_credits_balance || 0));
      setRows(Array.isArray(list?.rows) ? list.rows : []);
    } catch {
      /* silent retry */
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
    const t = setInterval(refresh, 60000);
    return () => clearInterval(t);
  }, [refresh]);

  const handlePause = async (id) => {
    try {
      await pauseBanner(id);
      toast.success('Banner paused');
      refresh();
    } catch (_err) {
      toast.error('Pause failed');
    }
  };
  const handleResume = async (id) => {
    try {
      await resumeBanner(id);
      toast.success('Banner resumed');
      refresh();
    } catch (_err) {
      toast.error('Resume failed');
    }
  };

  return (
    <div className="space-y-6" data-testid="subpage-advertise">
      {/* Big credits headline */}
      <section className="sc-glass-strong rounded-2xl p-5 md:p-7 relative overflow-hidden" data-testid="advertise-credits-headline">
        <div
          className="absolute -top-16 -right-16 w-56 h-56 pointer-events-none rounded-full"
          style={{
            background: 'radial-gradient(circle, rgba(255,182,18,0.18), transparent 60%)',
          }}
          aria-hidden="true"
        />
        <div className="relative z-10 flex items-center gap-4 flex-wrap">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center border"
            style={{
              background: 'rgba(255,182,18,0.06)',
              borderColor: 'rgba(255,182,18,0.28)',
            }}
          >
            <Megaphone className="w-6 h-6 text-[#FFC20E]" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--sc-text-mute)]">Your 1SC3 Ad Credits</div>
            <div className="sc-tabular font-display font-bold text-3xl md:text-4xl mt-1 leading-none sc-gold-text" data-testid="advertise-credits-balance">
              {credits.toLocaleString()}
            </div>
          </div>
          <div
            className="inline-flex items-start gap-2 px-3 py-2 rounded-lg border text-[11px] max-w-md"
            style={{
              background: 'rgba(0,0,0,0.32)',
              borderColor: 'rgba(255,182,18,0.18)',
              color: 'var(--sc-text-mute)',
            }}
          >
            <Sparkles className="w-3.5 h-3.5 text-[#FFC20E] shrink-0 mt-0.5" />
            <span className="leading-snug">
              Earn 1 credit per cent spent ($1 = 100 credits). Each credit redeems 1:1 into a banner impression on the homepage and member area.
            </span>
          </div>
        </div>
      </section>

      <BannerBuilder credits={credits} onCreated={refresh} />
      <MyBannersList rows={rows} onPause={handlePause} onResume={handleResume} />
    </div>
  );
};

export default AdvertiseSubpage;
